<template>
    <div>
        <input
            ref="autocomplete"
            :id="id"
            :name="name"
            :placeholder="placeholder"
            :class="inputClass"
            type="text"
            @focus="initAutocomplete"
            v-model="formattedAddress"
            @input="handleInput"
        />
        <p v-if="invalidAddressFormat" class="address-error">Invalid address format</p>
        <p v-if="countyRejected" class="address-error">{{ countyRejected }}</p>
    </div>
</template>

<script>
import { Loader } from '@googlemaps/js-api-loader';
import debounce from 'lodash.debounce';
import { parseUsAddress } from '../utils/usAddress';

export default {
    props: {
        id: {
            type: String,
            default: "autocomplete",
        },
        name: {
            type: String,
            default: "autocomplete",
        },
        placeholder: {
            type: String,
            default: "Enter your address",
        },
        inputClass: {
            type: String,
            default: "",
        },
        value: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            formattedAddress: this.value,
            autocomplete: null,
            invalidAddressFormat: false,
            debouncedHandleInput: null,
            isLoadingMaps: false,
            countyRejected: null,
            suggestionsShownEmitted: false,
        };
    },
    created() {
        this.debouncedHandleInput = debounce(this.processInput, 300);
    },
    beforeDestroy() {
        if (this.debouncedHandleInput) {
            this.debouncedHandleInput.cancel();
        }
    },
    watch: {
        value(newVal) {
            this.formattedAddress = newVal;
        },
    },
    methods: {
        initAutocomplete() {
            // Don't initialize if already exists or currently loading
            if (this.autocomplete || this.isLoadingMaps) {
                return;
            }

            // Check if Google Maps API is already loaded
            if (window.google && window.google.maps && window.google.maps.places) {
                this.createAutocomplete();
                return;
            }

            // Load Google Maps API if not already loaded
            this.isLoadingMaps = true;
            const loader = new Loader({
                apiKey: window.google_api_key,
                version: 'weekly',
                libraries: ['places']
            });

            loader.load()
                .then(() => {
                    this.createAutocomplete();
                })
                .catch((error) => {
                    console.error('Error loading Google Maps API:', error);
                })
                .finally(() => {
                    this.isLoadingMaps = false;
                });
        },

        createAutocomplete() {
            if (this.autocomplete || !this.$refs.autocomplete) {
                return;
            }

            try {
                this.autocomplete = new google.maps.places.Autocomplete(this.$refs.autocomplete, {
                    //allow only full address
                    types: ["street_address"],
                    componentRestrictions: { country: "us" },
                });

                this.autocomplete.addListener("place_changed", this.onPlaceChanged);
            } catch (error) {
                console.error('Error creating Google Maps Autocomplete:', error);
            }
        },

        onPlaceChanged() {
            // Cancel any in-flight typed-input parse so it cannot overwrite a
            // successful Places selection (race after pac-item click).
            this.debouncedHandleInput?.cancel();
            this.invalidAddressFormat = false;
            this.countyRejected = null;
            const place = this.autocomplete.getPlace();
            const formattedPlace = this.getFormatedPlace(place);

            // Reject the selection if Google resolved to a county rather than a
            // real city — this happens for unincorporated areas where the
            // `locality` component is absent and only `administrative_area_level_2`
            // is present.  Also reject if the place itself is typed as a county
            // (place.types includes administrative_area_level_2).
            const placeTypes = place.types || [];
            if (formattedPlace.cityIsCounty || placeTypes.includes('administrative_area_level_2')) {
                this.formattedAddress = '';
                this.countyRejected = 'That result is a county, not a street address. Please type your full street address and select a specific result from the list.';
                this.$emit('update:place', '', null);
                this.$nextTick(() => {
                    if (this.$refs.autocomplete) {
                        this.$refs.autocomplete.focus();
                    }
                });
                return;
            }

            this.formattedAddress = place.formatted_address;
            this.$emit("update:place", this.formattedAddress, formattedPlace);
            this.$emit("suggestion-accepted");
            this.suggestionsShownEmitted = false;
        },

        handleInput(event) {
            this.formattedAddress = event.target.value;
            this.countyRejected = null;
            this.$emit('typing');
            // Emit suggestions-shown once per typing session, when the input
            // is long enough that Google Places will likely surface suggestions.
            if (!this.suggestionsShownEmitted && event.target.value && event.target.value.length >= 3) {
                this.$emit('suggestions-shown');
                this.suggestionsShownEmitted = true;
            }
            if (!event.target.value) {
                this.suggestionsShownEmitted = false;
            }
            this.debouncedHandleInput(event.target.value);
        },

        processInput(inputValue) {
            const parsed = parseUsAddress(inputValue);
            this.invalidAddressFormat = !parsed;

            if (parsed) {
                this.formattedAddress = parsed.formatted;

                const formattedPlace = {
                    address: parsed.address,
                    city: parsed.city,
                    state: parsed.state,
                    zip: parsed.zip,
                };
                this.$emit("update:place", this.formattedAddress, formattedPlace);
                // User typed a fully-formatted address themselves rather than
                // selecting from Google's suggestion dropdown.
                this.$emit("suggestion-rejected");
                this.suggestionsShownEmitted = false;

                // Clear Google Autocomplete and blur input
                if (this.autocomplete) {
                    google.maps.event.clearInstanceListeners(this.autocomplete);
                    this.autocomplete.unbindAll();
                    google.maps.event.clearInstanceListeners(this.$refs.autocomplete);
                    this.autocomplete = null;
                }
                this.$refs.autocomplete.blur();
            } else {
                this.$emit("update:place", this.formattedAddress, null);
            }
        },

        getFormatedPlace(place) {
            const address_comp = place["address_components"];

            let streetNumber = this.getAddressComponentsValue(address_comp, "street_number");
            const streetName = this.getAddressComponentsValue(address_comp, "route");

            if(streetNumber != '') {
                streetNumber = streetNumber + ' ';
            }

            // Get city - prefer locality (actual city), fall back to sublocality, then county
            let city = this.getAddressComponentsValue(address_comp, "locality");
            let cityIsCounty = false;
            let county = this.getAddressComponentsValue(address_comp, "administrative_area_level_2");

            // If no locality, try sublocality (for places like NYC boroughs)
            if (!city) {
                city = this.getAddressComponentsValue(address_comp, "sublocality_level_1");
            }

            // If still no city, fall back to county (administrative_area_level_2)
            // This happens in unincorporated areas
            if (!city && county) {
                city = county;
                cityIsCounty = true;
            }

            return {
                address: streetNumber + streetName,
                city: city,
                county: county,
                cityIsCounty: cityIsCounty,
                state: this.getAddressComponentsValue(address_comp, "administrative_area_level_1", true),
                zip: this.getAddressComponentsValue(address_comp, "postal_code"),
            }
        },

      getAddressComponentsValue(addressComp, type, short = false) {
          let obj = addressComp.filter((comp) => comp.types.includes(type));
  
          if (!obj.length) {
            return "";
          }
  
          return obj[0][short ? "short_name" : "long_name"];
        }
    },
};
</script>

<style scoped>
.address-error {
    color: #dc2626;
    border: 1px solid #dc2626;
    padding: 0.5rem;
    margin-top: 0.5rem;
    border-radius: 0.25rem;
}

</style>